/**
 * 三阶段预览卡片组件
 * 显示护理方案的三个主要阶段（未解锁状态）
 * Requirements: 3.1, 3.2, 3.3
 */

import { useTranslation } from "react-i18next";
import { Lock, Droplets, Eye, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ThreeStagePreviewProps {
  /** 护理周期天数 */
  careDurationDays: number;
  /** 自定义类名 */
  className?: string;
}

export function ThreeStagePreview({
  careDurationDays,
  className,
}: ThreeStagePreviewProps) {
  const { t } = useTranslation("assessments");

  const stages = [
    {
      id: "stage-1",
      icon: Droplets,
      title: t("carePlan.preview.stage1.title", "第一阶段：急救处理"),
      duration: t("carePlan.preview.stage1.duration", "Day 0-1"),
      description: t(
        "carePlan.preview.stage1.description",
        "立即采取紧急措施，稳定鱼只状态"
      ),
      color: "text-red-500",
      bgColor: "bg-red-50 dark:bg-red-950/20",
      borderColor: "border-red-200 dark:border-red-800",
    },
    {
      id: "stage-2",
      icon: Eye,
      title: t("carePlan.preview.stage2.title", "第二阶段：密集护理"),
      duration: t(
        "carePlan.preview.stage2.duration",
        `Day 2-${Math.floor(careDurationDays * 0.6)}`
      ),
      description: t(
        "carePlan.preview.stage2.description",
        "每日监测，持续用药，观察恢复情况"
      ),
      color: "text-amber-500",
      bgColor: "bg-amber-50 dark:bg-amber-950/20",
      borderColor: "border-amber-200 dark:border-amber-800",
    },
    {
      id: "stage-3",
      icon: TrendingUp,
      title: t("carePlan.preview.stage3.title", "第三阶段：巩固恢复"),
      duration: t(
        "carePlan.preview.stage3.duration",
        `Day ${Math.floor(careDurationDays * 0.6) + 1}-${careDurationDays}`
      ),
      description: t(
        "carePlan.preview.stage3.description",
        "逐步减少用药，恢复正常饲养"
      ),
      color: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-950/20",
      borderColor: "border-green-200 dark:border-green-800",
    },
  ];

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Lock className="w-4 h-4 text-muted-foreground" />
          {t("carePlan.preview.title", "护理方案概览")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t(
            "carePlan.preview.subtitle",
            "解锁查看完整的每日护理计划和详细指导"
          )}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {stages.map((stage, index) => (
          <div
            key={stage.id}
            className={cn(
              "relative p-4 rounded-lg border transition-all",
              stage.bgColor,
              stage.borderColor,
              "hover:shadow-sm"
            )}
          >
            {/* 阶段编号 */}
            <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-background border-2 border-current flex items-center justify-center text-xs font-bold">
              {index + 1}
            </div>

            <div className="flex items-start gap-3">
              {/* 图标 */}
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  stage.bgColor,
                  stage.color
                )}
              >
                <stage.icon className="w-5 h-5" />
              </div>

              {/* 内容 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-sm">{stage.title}</h3>
                  <Badge
                    variant="outline"
                    className="text-xs border-current/30"
                  >
                    {stage.duration}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {stage.description}
                </p>
              </div>

              {/* 锁定图标 */}
              <Lock className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
            </div>
          </div>
        ))}

        {/* 底部提示 */}
        <div className="pt-2 text-center">
          <p className="text-xs text-muted-foreground">
            {t(
              "carePlan.preview.unlockHint",
              "解锁后可查看每日详细任务、用药剂量、观察要点等"
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default ThreeStagePreview;
